/**
 * Automatic Streak V2 Types for Frontend
 *
 * V2 streaks use a different scheduling model:
 * - totalTargetVolume instead of volumeMin/volumeMax
 * - timeframeMs instead of intervalSeconds
 * - scheduledCycles in executionLog for buy/sell cycle tracking
 */
import { DecimalSchema } from "../../decimal";
import { AUTOMATIC_STREAK_STATUS, AUTOMATIC_STREAK_STOP_REASON, AUTOMATIC_STREAK_CYCLE_SKIP_REASON } from "./config";
import { z } from "zod";

export const AUTOMATIC_STREAK_V2_TIMEFRAMES = {
  HOUR: "1h",
  EPOCH: "epoch",
} as const;
export type AutomaticStreakV2Timeframe =
  (typeof AUTOMATIC_STREAK_V2_TIMEFRAMES)[keyof typeof AUTOMATIC_STREAK_V2_TIMEFRAMES];

/**
 * Lifecycle status of a row in the autopilot queue. `IN` is the live pickable
 * pool; `OUT` is the audit trail of past leaves (preserved with a
 * `leaveReason`). Shared between the API domain entity, the wire schemas, and
 * the hub frontend so all three reference the same source of truth.
 */
export const AUTOMATIC_STREAK_QUEUE_ITEM_STATUS = {
  IN: "IN",
  OUT: "OUT",
} as const;

export type AutomaticStreakQueueItemStatus =
  (typeof AUTOMATIC_STREAK_QUEUE_ITEM_STATUS)[keyof typeof AUTOMATIC_STREAK_QUEUE_ITEM_STATUS];

export const AutomaticStreakQueueItemStatusSchema = z.enum(AUTOMATIC_STREAK_QUEUE_ITEM_STATUS);

/**
 * Reasons the autopilot queue evicts a user from a campaign. `USER_REQUESTED`
 * is a voluntary leave; every other value is a system-driven kick mapped from
 * a specific backend dramatic error. Shared with the API so both sides
 * reference the same source of truth (the backend's domain enum lives here).
 */
export const AUTOMATIC_STREAK_QUEUE_LEAVE_REASON = {
  USER_REQUESTED: "USER_REQUESTED",
  INSUFFICIENT_BALANCE: "INSUFFICIENT_BALANCE",
  EXCHANGE_ACCOUNT_DISCONNECTED: "EXCHANGE_ACCOUNT_DISCONNECTED",
  MISSING_SOCIAL_ACCOUNTS: "MISSING_SOCIAL_ACCOUNTS",
  INVALID_CONFIGURATION: "INVALID_CONFIGURATION",
} as const;

export type AutomaticStreakQueueLeaveReason =
  (typeof AUTOMATIC_STREAK_QUEUE_LEAVE_REASON)[keyof typeof AUTOMATIC_STREAK_QUEUE_LEAVE_REASON];

export const AutomaticStreakQueueLeaveReasonSchema = z.enum(AUTOMATIC_STREAK_QUEUE_LEAVE_REASON);

// Cycle order schema - represents a buy or sell order within a cycle
// Uses DecimalSchema to handle both raw DB values (Date/Decimal) and serialized values (string)
export const CycleOrderSchema = z.object({
  id: z.string().optional(), // Exchange order ID (filled on execution)
  createdAt: z.string().optional(), // Order creation time (filled on execution)
  orderSize: DecimalSchema.optional(), // Expected order size (may be undefined until scheduled)
  volumeUSD: DecimalSchema, // Target/actual volume in USD
});

export type CycleOrder = z.infer<typeof CycleOrderSchema>;

// Scheduled cycle schema - represents a buy/sell cycle. A cycle holds several
// buys and several sells (each ≥ the venue floor); the sides stay delta-neutral.
// Uses DecimalSchema to handle both raw DB values (Date/Decimal) and serialized values (string)
const ScheduledCycleObjectSchema = z.object({
  id: z.uuid(), // Unique cycle identifier
  scheduledAt: z.string(), // Planned execution time
  executedAt: z.string().optional(), // Actual execution time (filled on first buy)
  finishedAt: z.string().optional(), // Finished execution time (filled on last sell)
  targetVolume: DecimalSchema, // Target volume for this cycle (buy + sell)
  buyOrders: z.array(CycleOrderSchema).default([]), // Executed buy legs (empty until execution)
  sellOrders: z.array(CycleOrderSchema).default([]), // Executed sell legs (empty until execution)
  success: z.boolean().optional(), // Filled on execution
  isSkipped: z.boolean().optional(), // Filled on execution if skipped
  skipReason: z.nativeEnum(AUTOMATIC_STREAK_CYCLE_SKIP_REASON).optional(), // Reason for skipping (V2 only)
  error: z.string().optional(), // Filled on execution if failed
});

/**
 * Normalizes legacy single-order cycles (`{ buyOrder, sellOrder }`) written
 * before the multi-order split into the array shape, so in-flight streaks stay
 * loadable across deploy without a data migration.
 */
// Only an EXECUTED legacy order (carries an exchange `id`) becomes a leg; a pending cycle's
// planned `{ volumeUSD }` placeholder (no id) normalizes to an empty array. Detect by id, not
// truthiness — otherwise "already bought" is inferred for a cycle that never executed a buy.
const isExecutedLegacyOrder = (order: unknown): boolean =>
  !!order && typeof order === "object" && (order as Record<string, unknown>).id != null;

const normalizeLegacyCycleOrders = (value: unknown): unknown => {
  if (!value || typeof value !== "object") return value;
  const cycle = value as Record<string, unknown>;
  if (cycle.buyOrders !== undefined || cycle.sellOrders !== undefined) return value;
  return {
    ...cycle,
    buyOrders: isExecutedLegacyOrder(cycle.buyOrder) ? [cycle.buyOrder] : [],
    sellOrders: isExecutedLegacyOrder(cycle.sellOrder) ? [cycle.sellOrder] : [],
  };
};

export const ScheduledCycleSchema = z.preprocess(normalizeLegacyCycleOrders, ScheduledCycleObjectSchema);

export type ScheduledCycle = z.infer<typeof ScheduledCycleSchema>;

/**
 * A scheduled cycle enriched with streak context (campaign and epoch).
 */
export const UserScheduledCycleSchema = z.preprocess(
  normalizeLegacyCycleOrders,
  ScheduledCycleObjectSchema.extend({
    campaignName: z.string(),
    epochId: z.number(),
  }),
);

export type UserScheduledCycle = z.infer<typeof UserScheduledCycleSchema>;

// Execution log schema for V2
export const ExecutionLogV2Schema = z.object({
  scheduledCycles: z.array(ScheduledCycleSchema).optional(), // V2 format
  spreadThresholdPercent: z.number().optional(), // Persisted at creation for spread cost computation
});

export type ExecutionLogV2 = z.infer<typeof ExecutionLogV2Schema>;

/**
 * Streak summary response for V2 streaks.
 * Returns key streak data without full execution log details.
 */
export const StreakSummaryResponseSchemaV2 = z.object({
  id: z.string(),
  campaignName: z.string(),
  epochId: z.number(),
  pairSymbol: z.string(),
  totalTargetVolume: z.string(), // Total volume to trade across all cycles
  timeframeMs: z.number().nullable(), // Timeframe in milliseconds (1h or epoch duration)
  completedTrades: z.number(), // Number of completed cycles
  skippedCycles: z.number().optional(), // Cycles skipped due to spread threshold or volume cooldown
  status: z.enum(AUTOMATIC_STREAK_STATUS),
  scheduledStartTime: z.string(),
  actualStartTime: z.string().optional(),
  finishedAt: z.string().optional(),
  stopReason: z.enum(AUTOMATIC_STREAK_STOP_REASON).optional(),
  totalVolumeTraded: z.string(),
  createdAt: z.string(),
  version: z.literal("V2"), // Always "V2" for this schema
});

export type StreakSummaryResponseV2 = z.infer<typeof StreakSummaryResponseSchemaV2>;

/**
 * Full streak response for V2 streaks.
 * Includes detailed execution log with scheduled cycles.
 */
export const StreakResponseV2Schema = z.object({
  id: z.string(),
  hubUserId: z.number(),
  campaignName: z.string(),
  epochId: z.number(),
  exchangeAccountId: z.number(),
  pairSymbol: z.string(),
  totalTargetVolume: z.string(), // Total volume to trade across all cycles
  timeframeMs: z.number().nullable(), // Timeframe in milliseconds (1h or epoch duration)
  scheduledStartTime: z.string(),
  actualStartTime: z.string().optional(),
  status: z.enum(AUTOMATIC_STREAK_STATUS),
  completedTrades: z.number(),
  skippedCycles: z.number(), // Cycles skipped due to spread threshold or volume cooldown
  totalVolumeTraded: z.string(),
  executionLog: ExecutionLogV2Schema.optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  finishedAt: z.string().optional(),
  stopReason: z.enum(AUTOMATIC_STREAK_STOP_REASON).optional(),
  version: z.literal("V2"), // Always "V2" for this schema
});

export type StreakResponseV2 = z.infer<typeof StreakResponseV2Schema>;

/**
 * Schema for creating an automatic streak.
 *
 * This schema provides loose sanity-check validation only.
 * Actual business limits (min/max trades, volume, intervals) are validated
 * in StreakVolumeValidatorService using CMS config as the single source of truth.
 *
 * V2 always runs for the full epoch duration - timeframe is no longer configurable.
 * If nextEpochId is provided, a second streak is created for that epoch.
 * scheduledStartTime is calculated server-side as creation time + 1 minute.
 */
export const CreateStreakRequestSchemaV2 = z
  .object({
    campaignName: z.string().min(1, "Campaign name is required"),
    epochId: z.number().int().nonnegative("Epoch ID must be a non-negative integer"),
    nextEpochId: z.number().int().nonnegative("Next epoch ID must be a non-negative integer").optional(),
    exchangeAccountId: z.number().int().positive("Exchange account ID must be a positive integer"),
    pairSymbol: z.string().min(1, "Pair symbol is required"),
    // Sanity checks only - actual limits come from CMS config
    totalTargetVolume: DecimalSchema,
  })
  .refine((data) => data.totalTargetVolume.greaterThan(0), {
    message: "Volume per cycle must be positive",
    path: ["volumePerCycle"],
  })
  .refine((data) => data.totalTargetVolume.lessThanOrEqualTo(100000), {
    message: "Volume per cycle exceeds sanity limit",
    path: ["volumePerCycle"],
  });

export type CreateStreakRequestV2 = z.infer<typeof CreateStreakRequestSchemaV2>;

/**
 * Schema for opting into the V2 autopilot streak queue. The queued entry is
 * later materialized into a real streak when the user is picked for a free
 * slot in the current epoch. Joining is idempotent on (user, campaign).
 */
export const JoinStreakQueueRequestSchemaV2 = z
  .object({
    campaignName: z.string().min(1, "Campaign name is required"),
    exchangeAccountId: z.number().int().positive("Exchange account ID must be a positive integer"),
    pairSymbol: z.string().min(1, "Pair symbol is required"),
    totalTargetVolume: DecimalSchema,
    /**
     * Priority tickets to spend on the queue item — each buys one prioritized
     * epoch during queue picking. Also tops up an already-IN row. Omitted/0 =
     * plain join.
     */
    priorityTicketsToSpend: z.number().int().min(0).max(1000).optional(),
  })
  .refine((data) => data.totalTargetVolume.greaterThan(0), {
    message: "Total target volume must be positive",
    path: ["totalTargetVolume"],
  })
  .refine((data) => data.totalTargetVolume.lessThanOrEqualTo(100000), {
    message: "Total target volume exceeds sanity limit",
    path: ["totalTargetVolume"],
  });

export type JoinStreakQueueRequestV2 = z.infer<typeof JoinStreakQueueRequestSchemaV2>;

export const JoinStreakQueueResponseSchemaV2 = z.object({
  status: z.string(),
});

export type JoinStreakQueueResponseV2 = z.infer<typeof JoinStreakQueueResponseSchemaV2>;

export const LeaveStreakQueueResponseSchemaV2 = z.object({
  status: z.string(),
});

export type LeaveStreakQueueResponseV2 = z.infer<typeof LeaveStreakQueueResponseSchemaV2>;

/**
 * Snapshot of the authenticated user's queue row for a campaign. All four
 * fields are `null` when the user has never opted in. Otherwise:
 *
 * - `status: "IN"` — currently queued; `joinedAt` is set, `leaveReason` and
 *   `leftAt` are `null`.
 * - `status: "OUT"` — previously joined, currently out; `joinedAt`,
 *   `leaveReason` and `leftAt` are all set. The frontend decides what to
 *   render based on `leaveReason` (e.g. `USER_REQUESTED` → silent default,
 *   otherwise → kicked banner explaining why).
 *
 * Note: `status` is widened to `string | null` rather than the literal union
 * `"IN" | "OUT" | null` so the response can share the API client's RFC 9457
 * problem-details discriminator (which also keys on a `status` field).
 */
export const GetUserStreakQueueItemResponseSchemaV2 = z.object({
  status: z.string().nullable(),
  joinedAt: z.string().nullable(),
  leaveReason: AutomaticStreakQueueLeaveReasonSchema.nullable(),
  leftAt: z.string().nullable(),
  /** Remaining ticket-bought prioritized epochs on the row. 0 when unprioritized or no row. */
  priorityCounter: z.number().int().nonnegative(),
});

export type GetUserStreakQueueItemResponseV2 = z.infer<typeof GetUserStreakQueueItemResponseSchemaV2>;

/** Balance of the user's unconsumed autopilot priority tickets (earned from paid Arena competitions). */
export const GetPriorityTicketBalanceResponseSchemaV2 = z.object({
  totalTickets: z.number().int().nonnegative(),
});

export type GetPriorityTicketBalanceResponseV2 = z.infer<typeof GetPriorityTicketBalanceResponseSchemaV2>;

/** Body for the queue priority top-up — spend more tickets while already IN. */
export const IncreaseQueuePriorityRequestSchemaV2 = z.object({
  ticketsToSpend: z.number().int().min(1).max(1000),
});

export type IncreaseQueuePriorityRequestV2 = z.infer<typeof IncreaseQueuePriorityRequestSchemaV2>;

export const IncreaseQueuePriorityResponseSchemaV2 = z.object({
  status: z.string(),
});

export type IncreaseQueuePriorityResponseV2 = z.infer<typeof IncreaseQueuePriorityResponseSchemaV2>;

/**
 * Body for the admin manual ticket grant. NOT idempotent by default — every
 * call mints `count` fresh tickets for the user. Pass `grantId` (any UUID) as
 * an idempotency key when the caller may retry: two grants with the same
 * grantId mint once. `reason` is the audit note stored on each ticket's
 * metadata.
 */
export const AdminGrantPriorityTicketsRequestSchemaV2 = z.object({
  hubUserId: z.number().int().positive(),
  count: z.number().int().min(1).max(1000),
  reason: z.string().min(1).max(500).optional(),
  grantId: z.string().uuid().optional(),
});

export type AdminGrantPriorityTicketsRequestV2 = z.infer<typeof AdminGrantPriorityTicketsRequestSchemaV2>;

export const AdminGrantPriorityTicketsResponseSchemaV2 = z.object({
  status: z.string(),
  /** The user's unconsumed ticket balance after the grant. */
  totalTickets: z.number().int().nonnegative(),
});

export type AdminGrantPriorityTicketsResponseV2 = z.infer<typeof AdminGrantPriorityTicketsResponseSchemaV2>;

/**
 * One row of the autopilot queue, flattened for admin tooling. `commitLog`
 * lists every epoch the user was previously picked for, oldest first.
 */
export const AdminStreakQueueItemDtoSchema = z.object({
  id: z.string(),
  hubUserId: z.number(),
  campaignName: z.string(),
  exchangeAccountId: z.number(),
  pairSymbol: z.string(),
  totalTargetVolume: z.string(),
  status: z.string(), // "IN" | "OUT"
  leaveReason: z.string().nullable(),
  commitLog: z.array(
    z.object({
      epochId: z.number(),
      committedAt: z.string(),
      /** True when the pick consumed a priority-ticket epoch. Absent on legacy entries. */
      isPrioritized: z.boolean().optional(),
    }),
  ),
  /** Remaining ticket-bought prioritized epochs on the row. */
  priorityCounter: z.number().int().nonnegative(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AdminStreakQueueItemDto = z.infer<typeof AdminStreakQueueItemDtoSchema>;

/**
 * Backend-side filter for the admin queue-items listing. Pagination, `total`,
 * and the returned `items` reflect the active filter; the per-status counts
 * (`inQueueCount`, `kickedCount`, `voluntaryLeftCount`) are always unfiltered
 * so the top-of-card tiles can show whole-queue breakdowns regardless of which
 * slice is currently displayed.
 */
export const ADMIN_STREAK_QUEUE_ITEM_FILTER = {
  ALL: "all",
  IN: "in",
  KICKED: "kicked",
  VOLUNTARY: "voluntary",
} as const;

export type AdminStreakQueueItemFilter =
  (typeof ADMIN_STREAK_QUEUE_ITEM_FILTER)[keyof typeof ADMIN_STREAK_QUEUE_ITEM_FILTER];

export const AdminStreakQueueItemFilterSchema = z.enum(ADMIN_STREAK_QUEUE_ITEM_FILTER);

export const AdminGetStreakQueueItemsByCampaignResponseSchema = z.object({
  items: z.array(AdminStreakQueueItemDtoSchema),
  total: z.number().int().nonnegative(),
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  inQueueCount: z.number().int().nonnegative(),
  kickedCount: z.number().int().nonnegative(),
  voluntaryLeftCount: z.number().int().nonnegative(),
});

export type AdminGetStreakQueueItemsByCampaignResponse = z.infer<
  typeof AdminGetStreakQueueItemsByCampaignResponseSchema
>;

/**
 * Summary of a user with an active V2 streak.
 * Used for admin views to list users with streaks for an epoch.
 */
export const UserWithStreakSummarySchema = z.object({
  hubUserId: z.number(),
  userName: z.string().optional(),
  streakId: z.string(),
  status: z.nativeEnum(AUTOMATIC_STREAK_STATUS),
  completedTrades: z.number(),
  totalVolumeTraded: z.string(),
});

export type UserWithStreakSummary = z.infer<typeof UserWithStreakSummarySchema>;

/**
 * Simplified user info for autopilot user lists.
 * Only contains ID and defi address, no streak data.
 * Used when streak details are fetched separately.
 */
export const AutopilotUserSchema = z.object({
  id: z.number(),
  defiAddress: z.string().nullable(),
});

export type AutopilotUser = z.infer<typeof AutopilotUserSchema>;

/**
 * Trade from autopilot cycles (executed or scheduled).
 * Used for admin analytics views.
 */
export const AutomaticTradeSchema = z.object({
  timestamp: z.string(),
  volumeUsd: z.string(),
  side: z.enum(["buy", "sell"]),
  isExecuted: z.boolean(),
});

export type AutomaticTrade = z.infer<typeof AutomaticTradeSchema>;

/**
 * User info for executed trades response.
 */
export const UserInfoSchema = z.object({
  id: z.number(),
  defiAddress: z.string().nullable(),
});

export type UserInfo = z.infer<typeof UserInfoSchema>;

/**
 * User automatic trades response.
 * Groups trades by user with user info.
 */
export const UserAutomaticTradesSchema = z.object({
  user: UserInfoSchema,
  trades: z.array(AutomaticTradeSchema),
  /** Scheduled start times from all streaks associated with this user (one per streak) */
  scheduledStartTimes: z.array(z.string()),
});

export type UserAutomaticTrades = z.infer<typeof UserAutomaticTradesSchema>;

/**
 * Response for epochs with V2 streaks.
 * Returns epoch IDs that have at least one streak for a campaign.
 */
export const EpochsWithStreaksResponseSchema = z.object({
  epochIds: z.array(z.number()),
});

export type EpochsWithStreaksResponse = z.infer<typeof EpochsWithStreaksResponseSchema>;

/**
 * Response for users with V2 streaks in an epoch.
 * Returns list of users that have at least one streak for a campaign/epoch.
 */
export const AutopilotUsersResponseSchema = z.object({
  users: z.array(AutopilotUserSchema),
});

export type AutopilotUsersResponse = z.infer<typeof AutopilotUsersResponseSchema>;

/**
 * Response for automatic trades.
 * Returns trades grouped by user for analytics.
 */
export const AutopilotTradesResponseSchema = z.object({
  users: z.array(UserAutomaticTradesSchema),
});

export type AutopilotTradesResponse = z.infer<typeof AutopilotTradesResponseSchema>;

/**
 * Response for epoch limit status check.
 * Returns whether the autopilot limit for the current epoch has been reached.
 */
export const EpochLimitStatusResponseSchema = z.object({
  /** Whether the epoch limit is reached (cannot create more autopilots) */
  isLimitReached: z.boolean(),
});

export type EpochLimitStatusResponse = z.infer<typeof EpochLimitStatusResponseSchema>;
